import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { CommandPalette } from '@/components/CommandPalette';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';
import type { DashboardSummary } from '@/types';

export function Dashboard() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const { idToken } = useCognitoAuth();

  useEffect(() => {
    if (!idToken) return;
    async function fetchData() {
      try {
        const code = ErrorCode.FETCH_DASHBOARD;
        const [tb, accountsResp, bankAccounts, taxCodes, matched] = await Promise.all([
          api.get('/api/reports/trial-balance', { errorCode: code, token: idToken! }),
          api.get<{ items?: { type: string; id: number }[] }>('/api/accounts', { errorCode: code, token: idToken! }),
          api.get<unknown[]>('/api/entities/bank-accounts', { errorCode: code, token: idToken! }),
          api.get<unknown[]>('/api/entities/tax-codes', { errorCode: code, token: idToken! }),
          api.get<{ total_bank_records: number }>('/api/reconciliation/unmatched', { errorCode: code, token: idToken! }).catch(() => null),
        ]);

         
        const accounts = (accountsResp as { items?: { type: string; id: number }[] }).items ?? [];
        const summaryData = {
          bank_accounts_count: (bankAccounts).length,
          total_accounts: accounts.length,
          tax_codes_count: (taxCodes).length,
          unreconciled_count: matched?.total_bank_records ?? 0,
          trial_balance: tb,
          accounts_by_type: accounts.reduce((acc: Record<string, number>, a: { type: string }) => {
            acc[a.type] = (acc[a.type] ?? 0) + 1;
            return acc;
          }, {} as Record<string, number>),
        } as DashboardSummary;

        setSummary(summaryData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }
    setLoading(true);
    void fetchData();
  }, [retryKey, idToken]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const handleOpenCommandPalette = () => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-6 animate-pulse">
        <div className="flex justify-between items-center pb-4 border-b border-border">
          <div>
            <div className="h-8 w-36 bg-muted rounded mb-2" />
            <div className="h-4 w-56 bg-muted rounded" />
          </div>
          <div className="h-9 w-36 bg-muted rounded-md" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-card rounded-lg border border-border p-6 space-y-3">
              <div className="h-4 w-24 bg-muted rounded" />
              <div className="h-8 w-28 bg-muted rounded" />
              <div className="h-3 w-16 bg-muted rounded" />
            </div>
          ))}
        </div>
        <div className="bg-card rounded-lg border border-border p-6 space-y-4">
          <div className="flex justify-between items-center">
            <div className="h-5 w-36 bg-muted rounded" />
            <div className="h-8 w-32 bg-muted rounded-md" />
          </div>
          <div className="h-16 bg-muted rounded-lg" />
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="text-center space-y-2">
                <div className="h-4 w-20 bg-muted rounded mx-auto" />
                <div className="h-7 w-24 bg-muted rounded mx-auto" />
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map(i => (
            <div key={i} className="bg-card rounded-lg border border-border p-6 space-y-3">
              <div className="flex justify-between items-center">
                <div className="h-5 w-28 bg-muted rounded" />
                <div className="h-7 w-16 bg-muted rounded" />
              </div>
              {[1, 2].map(j => (
                <div key={j} className="flex justify-between items-center py-2">
                  <div className="h-4 w-20 bg-muted rounded" />
                  <div className="h-4 w-8 bg-muted rounded" />
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="bg-card rounded-lg border border-border p-6 space-y-3">
          <div className="h-5 w-28 bg-muted rounded" />
          <div className="flex gap-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-8 w-24 bg-muted rounded-md" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm mb-4">{error}</div>
        <button className="text-sm text-primary hover:underline" onClick={() => { setError(null); setRetryKey(k => k + 1); }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <CommandPalette />
      
      <div className="flex justify-between items-center pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Your financial overview at a glance</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleOpenCommandPalette}>
            ⌘ K · Quick actions
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <i className="bi bi-cash-stack text-lg text-primary"></i>
              Cash & Bank
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {formatCurrency(Number(summary?.trial_balance?.accounts?.find((a) => a.type === 'ASSET')?.debit ?? 0))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary?.bank_accounts_count ?? 0} accounts
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <i className="bi bi-graph-up text-lg text-green-600"></i>
              Revenue (YTD)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(
                summary?.trial_balance?.accounts
                  ?.filter((a) => a.type === 'REVENUE')
                  .reduce((sum, a) => sum + Number(a.credit), 0) ?? 0
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">On track</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <i className="bi bi-graph-down-arrow text-lg text-red-600"></i>
              Expenses (YTD)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(
                summary?.trial_balance?.accounts
                  ?.filter((a) => a.type === 'EXPENSE')
                  .reduce((sum, a) => sum + Number(a.debit), 0) ?? 0
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Within budget</p>
          </CardContent>
        </Card>

        <Card className="bg-muted/50 border-primary/20">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <i className="bi bi-lightbulb text-lg text-primary"></i>
              AI Insights
              <Badge variant="secondary" className="ml-auto">
                <i className="bi bi-sparkles mr-1"></i>
                3 new
              </Badge>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Your tax filing deadline is in <strong>28 days</strong>. GST/HST remittance of <strong>$3,250.00</strong> is due.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <i className="bi bi-sliders text-primary"></i>
              Trial Balance Status
            </CardTitle>
          </div>
          <Link to="/reports/trial-balance">
            <Button variant="outline" size="sm">View Full Report</Button>
          </Link>
        </CardHeader>
        <CardContent>
          {summary?.trial_balance.is_balanced ? (
            <div className="flex items-center gap-3 mb-4">
              <i className="bi bi-check-circle text-xl text-success flex-shrink-0"></i>
              <div>
                <div className="font-semibold text-success">Books are balanced</div>
                <div className="text-sm text-muted-foreground">All debits equal credits</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 mb-4">
              <i className="bi bi-exclamation-triangle-fill text-xl text-danger flex-shrink-0"></i>
              <div>
                <div className="font-semibold text-danger">Books are out of balance</div>
                <div className="text-sm text-danger">
                  Difference: {formatCurrency(Math.abs(Number(summary?.trial_balance.difference ?? 0)))}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 items-center">
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-1">Total Debits</div>
              <div className="text-xl font-mono font-bold">
                {formatCurrency(Number(summary?.trial_balance.total_debits ?? 0))}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="w-px h-12 bg-border" />
            </div>
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-1">Total Credits</div>
              <div className="text-xl font-mono font-bold">
                {formatCurrency(Number(summary?.trial_balance.total_credits ?? 0))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <i className="bi bi-clipboard-data text-primary"></i>
              Account Summary
            </CardTitle>
            <Link to="/accounts">
              <Button variant="ghost" size="sm">View All</Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(summary?.accounts_by_type ?? {}).map(([type, count]) => (
                <div key={type} className="flex justify-between items-center py-2 border-b last:border-0">
                  <span className="text-sm">
                    {type.charAt(0) + type.slice(1).toLowerCase()}
                  </span>
                  <span className="font-mono font-medium">{count}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <i className="bi bi-hourglass-split text-primary"></i>
              Pending Items
            </CardTitle>
            <Link to="/reconciliation">
              <Button variant="ghost" size="sm">View All</Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted">
                <div className="flex items-center gap-2">
                  <i className={`bi ${summary?.unreconciled_count && summary.unreconciled_count > 0 ? 'bi-exclamation-circle text-amber-600' : 'bi-check-circle text-green-600'}`}></i>
                  <span className="text-sm">Unreconciled Transactions</span>
                </div>
                <span className="font-mono font-medium">{summary?.unreconciled_count ?? 0}</span>
              </div>

              <div className="flex justify-between items-center p-3 rounded-lg bg-muted">
                <div className="flex items-center gap-2">
                  <i className="bi bi-bar-chart text-muted-foreground"></i>
                  <span className="text-sm">Tax Codes Active</span>
                </div>
                <span className="font-mono font-medium">{summary?.tax_codes_count ?? 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-lightning-charge text-primary"></i>
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Link to="/reports/trial-balance">
              <Button variant="outline" size="sm">Verify Books</Button>
            </Link>
            <Link to="/reports/tax-summary">
              <Button variant="outline" size="sm">Tax Summary</Button>
            </Link>
            <Link to="/reconciliation">
              <Button variant="outline" size="sm">Reconcile</Button>
            </Link>
            <Link to="/journal">
              <Button variant="outline" size="sm">Journal Entry</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
